import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import PenOutlineIcon from "@src/lib/icons/18px/pen-outline";
import { OpusAgentChatTurnStatus } from "@src/types.generated";
import { OpusAgentErrorBubble } from "./OpusAgentErrorBubble";
import { OpusAgentCitations } from "./OpusAgentCitations";
import { OpusAgentEventBlock } from "./OpusAgentEventBlock";
import {
  foldToolCallEvents,
  getEventKind,
  groupEventBlocks,
  highestEventIndex,
} from "./events";
import type { OpusAgentTurnState } from "./useOpusAgentChat";

interface OpusAgentTurnMessageProps {
  turn: OpusAgentTurnState;
  turnIndex: number;
  onRetry: (message: string) => void;
  onResumePolling: (turnId: string) => void;
  onEditMessage?: (turnId: string, newMessage: string) => Promise<boolean>;
}

const formatElapsed = (seconds: number): string =>
  seconds < 60
    ? `${seconds}s`
    : `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, "0")}s`;

// Quiet reassurance while a turn is RUNNING and nothing else animates
// (before the first event, between slices, during long thinking after a tool
// settled): a pulse plus how long since the agent was last heard from.
const WorkingRow: React.FC<{ sinceMs: number }> = ({ sinceMs }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);
  const seconds = Math.max(0, Math.floor((now - sinceMs) / 1000));
  return (
    <div
      data-testid="opus-agent-working-row"
      className="flex items-center gap-1.5"
    >
      <Loader2 className="size-3 shrink-0 animate-spin text-muted-foreground" />
      <Text variant="fine-print-title" className="text-muted-foreground">
        {`Working · ${formatElapsed(seconds)} since last update`}
      </Text>
    </div>
  );
};

export const OpusAgentTurnMessage: React.FC<OpusAgentTurnMessageProps> = ({
  turn,
  turnIndex,
  onRetry,
  onResumePolling,
  onEditMessage,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editFailed, setEditFailed] = useState(false);
  const pollLost =
    !!turn.pollFailed && turn.status === OpusAgentChatTurnStatus.Running;
  const isRunning =
    turn.status === OpusAgentChatTurnStatus.Running && !pollLost;
  // A FAILED turn ending in an error event already shows that error bubble —
  // which then carries the turn's one Try-again (see foldedEvents below).
  const showGenericError =
    pollLost ||
    turn.sendFailed ||
    (turn.status === OpusAgentChatTurnStatus.Failed &&
      !turn.events.some((event) => event.payload.error));
  const foldedEvents = foldToolCallEvents(turn.events);
  const renderEvent = (event: OpusAgentTurnState["events"][number]) => (
    <OpusAgentEventBlock
      key={event.id}
      event={event}
      turnStatus={turn.status}
      onRetry={
        event.id === retryEventId ? () => onRetry(turn.userMessage) : undefined
      }
    />
  );
  // The retry affordance for a server-side failure lives on its error
  // bubble: only when the failure is the turn's FINAL state (an error event
  // on a turn that recovered, succeeded, or was aborted offers no retry),
  // and on the last error event only — one Try again per turn.
  const retryEventId =
    turn.status === OpusAgentChatTurnStatus.Failed
      ? [...foldedEvents]
          .reverse()
          .find((event) => getEventKind(event.payload) === "error")?.id
      : undefined;
  // When we last heard from the agent, by CLIENT receipt time: reset whenever
  // a new (higher) event index lands; the baseline is this row's mount — the
  // send itself for an optimistic turn, "since we started listening" for a
  // hydrated one. Server timestamps are never used for this.
  const maxEventIndex = highestEventIndex(turn.events);
  const lastHeardRef = useRef({ index: maxEventIndex, at: Date.now() });
  if (maxEventIndex > lastHeardRef.current.index) {
    lastHeardRef.current = { index: maxEventIndex, at: Date.now() };
  }
  // Edit-and-resend needs a server-side turn: optimistic and failed sends
  // (retry covers those) can't be rewound.
  const canEdit = !!onEditMessage && !turn.pending && !turn.sendFailed;

  const startEditing = () => {
    setDraft(turn.userMessage);
    setEditFailed(false);
    setIsEditing(true);
  };

  const submitEdit = async () => {
    if (!onEditMessage || saving) return;
    const trimmed = draft.trim();
    if (!trimmed || trimmed === turn.userMessage) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    const edited = await onEditMessage(turn.id, trimmed);
    setSaving(false);
    if (edited) {
      setIsEditing(false);
    } else {
      setEditFailed(true);
    }
  };

  return (
    <>
      {isEditing ? (
        <div className="flex w-full max-w-2xl flex-col items-end gap-1">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={2}
            autoFocus
            data-testid={`edit-user-message-input-${turnIndex}`}
            className="w-full resize-none rounded-xl border border-input bg-white p-2 text-[14px] leading-[20px] font-normal tracking-[-0.006em] outline-none focus:ring-0"
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                submitEdit();
              }
              if (event.key === "Escape") setIsEditing(false);
            }}
          />
          {editFailed && (
            <Text variant="fine-print-title" className="text-red-8">
              Could not update this message. Try again.
            </Text>
          )}
          <div className="flex gap-1">
            <Button
              variant="secondary"
              size="xs"
              onClick={() => setIsEditing(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              size="xs"
              onClick={submitEdit}
              disabled={saving || !draft.trim()}
              data-testid={`edit-user-message-save-${turnIndex}`}
            >
              Send
            </Button>
          </div>
        </div>
      ) : (
        <div className="group flex w-full max-w-2xl items-center justify-end gap-1">
          {canEdit && (
            <Button
              variant="ghost"
              size="icon-xs"
              className="shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
              onClick={startEditing}
              title="Edit message"
              data-testid={`edit-user-message-${turnIndex}`}
            >
              <PenOutlineIcon className="size-3 text-muted-foreground" />
            </Button>
          )}
          <div className="max-w-[80%] rounded-[20px] bg-gray-alpha-4 px-4 py-2">
            <Text variant="compact-description" multiline>
              {turn.userMessage}
            </Text>
          </div>
        </div>
      )}
      <div data-testid={`ai-message-${turnIndex}`} className="my-2 w-full">
        <Text variant="compact-description" multiline>
          {/* One spacing token for every block seam (row group ↔ text ↔ row
              group): gap-2, the dense markdown's paragraph gap, so the first
              paragraph sits no closer to the rows than to the next paragraph.
              Rows inside a group stay tight (gap-1). */}
          <div
            className="flex flex-col gap-2 break-words"
            data-testid={`ai-message-content-${turnIndex}`}
          >
            {groupEventBlocks(foldedEvents).map((group, groupIndex, groups) =>
              group.kind === "rows" ? (
                <div
                  key={group.key}
                  className="flex flex-col gap-1"
                  data-testid="opus-agent-activity-rows"
                >
                  {group.events.map((event) => renderEvent(event))}
                  {isRunning && groupIndex === groups.length - 1 && (
                    <WorkingRow sinceMs={lastHeardRef.current.at} />
                  )}
                </div>
              ) : (
                renderEvent(group.event)
              ),
            )}
            {isRunning &&
              groupEventBlocks(foldedEvents).at(-1)?.kind !== "rows" && (
                <WorkingRow sinceMs={lastHeardRef.current.at} />
              )}
          </div>
          {turn.status === OpusAgentChatTurnStatus.Succeeded &&
            turn.citations.length > 0 && (
              <OpusAgentCitations citations={turn.citations} />
            )}
        </Text>
        {turn.status === OpusAgentChatTurnStatus.Aborted && (
          <Text
            variant="fine-print-title"
            className="mt-1 block text-muted-foreground"
          >
            Stopped
          </Text>
        )}
        {showGenericError && (
          <OpusAgentErrorBubble
            message={
              pollLost
                ? "Lost connection while waiting for this response. It may still be running."
                : undefined
            }
            onRetry={() =>
              pollLost ? onResumePolling(turn.id) : onRetry(turn.userMessage)
            }
          />
        )}
      </div>
    </>
  );
};
